<template>
    <v-card>
      <v-stepper v-model="currentStep">
        <stepper-header :current-step="currentStep" />
  
        <v-stepper-window>
          <select-schedule-step
            v-if="currentStep === '1'"
            v-model:selected-schedule="selectedSchedule"
            :schedules="schedules"
            :contains-tasks="selectedScheduleContainsTasks"
            @continue="currentStep = '2'"
            @update:selected-time-range-template="handleTimeRangeTemplateUpdate"
          />
  
          <upload-step
            v-if="currentStep === '2'"
            v-model:nested-tasks="nestedTasks"
            v-model:selected-schedule="selectedSchedule"
            :selected-time-range-template="selectedTimeRangeTemplate"
            @update:processed-tasks="updateNestedTasks"
            @update:task-sets="updateTaskSets"
            @update:task-key="uploadTaskKey = $event"
            @back="currentStep = '1'"
            @continue="currentStep = '3'"
          />
  
          <review-structure-step
            v-if="currentStep === '3'"
            v-model:nested-tasks="nestedTasks"
            v-model:display-tasks="displayTasks"
            :p6-calendars="p6Calendars"
            v-model:changes="changes"
            :selected-schedule="selectedSchedule"
            :relationship-summary="relationshipSummary"
            :pruned-empty-wbs-count="prunedEmptyWbsCount"
            :duration-summary="durationSummary"
            :time-range-editability="timeRangeEditability"
            v-model:selected-items="selectedItems"
            v-model:open-items="openItems"
            @back="currentStep = '2'"
            @update:editable-time-range-ids="editableTimeRangeIds = $event"
            @import="importTasksToSchedules"
          />
        </v-stepper-window>
      </v-stepper>

      <calendar-import-dialog
        v-model="showCalendarDialog"
        :p6-calendars="p6Calendars"
        @confirmed="handleCalendarsConfirmed"
        @skipped="handleCalendarsSkipped"
      />

      <import-progress-dialog
        v-model="importingTasks"
        :completed="importCompleted"
        :error="importError"
        @done="handleImportComplete"
        @close="handleImportFailed"
      />
    </v-card>
  </template>
  
  <script setup>
  import { ref, computed, watch, shallowRef } from 'vue';
  import { useAppStore } from '@/stores/app';
  import { useSettingsStore } from '@/stores/settings';
  import { useScheduleStore } from '@/stores/schedule';
  import StepperHeader from './steps/StepperHeader.vue';
  import SelectScheduleStep from './steps/SelectScheduleStep.vue';
  import UploadStep from './steps/UploadStep.vue';
  import ReviewStructureStep from './steps/ReviewStructureStep.vue';
  import ImportProgressDialog from './dialogs/ImportProgressDialog.vue';
  import CalendarImportDialog from './dialogs/CalendarImportDialog.vue';
  import { ScheduleImporter } from '@/utils/ScheduleImporter';
  import { P6_TASK_KEY } from '@/utils/XMLTaskParser';
  import { CalendarImporter } from '@/utils/CalendarImporter';
  import { useTaskStore } from '@/stores/task';
  import { applyTaskKey, applyTaskLocks, deriveTimeRangeEditability, indexTasksById } from '@/utils/TaskLockUtils';
  import { extractApiErrorMessage } from '@/utils/ApiErrorUtils';

  const appStore = useAppStore();
  const settingsStore = useSettingsStore();
  const scheduleStore = useScheduleStore();
  const taskStore = useTaskStore();

  const currentStep = ref('1');
  const selectedScheduleContainsTasks = ref(false);
  const importingTasks = ref(false);
  const importCompleted = ref(false);
  const importError = ref(null);
  // shallowRef: these hold entire imported task trees (tens of thousands of
  // objects on large P6 files). Deep reactivity would proxy every node for no
  // benefit — they are only ever replaced wholesale, never mutated for the UI.
  const nestedTasks = shallowRef([]);
  const displayTasks = shallowRef([]);
  const changes = shallowRef([]);
  const selectedSchedule = ref(null);
  const selectedItems = ref([]);
  const selectedTimeRangeTemplate = ref(null);
  const openItems = ref([]);
  const taskSetsPayload = ref({});

  // P6 calendar import state (populated only for .xml uploads)
  const p6Calendars = ref([]);
  const taskCalendarLinks = ref(null);
  const showCalendarDialog = ref(false);
  // Lock keys the commit must present (P6 imports lock their tasks with 'p6')
  const commitKeys = ref([]);
  // Optional user-entered lock key from the upload step: stamped on every
  // task at commit time (replacing the processed key) and presented in the
  // commit's `keys` list alongside the processing keys, so tasks locked by a
  // previous keyed import may be updated. Applied at commit rather than at
  // parse time so changing it never re-processes the uploaded file.
  const uploadTaskKey = ref(null);
  // What the P6 relationship import kept/dropped; shown on the review step
  const relationshipSummary = ref(null);
  // WBS nodes dropped for having no activities; shown on the review step
  const prunedEmptyWbsCount = ref(0);
  // Committed durations checked against P6's own; shown on the review step
  const durationSummary = ref(null);
  // Which time ranges the upload's NEW tasks may edit, confirmed in the Save
  // Tasks dialog. Derived from the existing tasks: uniform settings
  // pre-select silently, disagreements show as 'mixed' for the user to
  // resolve. Null when the upload adds no tasks (nothing to confirm).
  const timeRangeEditability = shallowRef(null);
  const editableTimeRangeIds = ref([]);

  const schedules = computed(() => scheduleStore.getSchedules);
  const settings = computed(() => settingsStore.settings);

  const commitTitle = computed({
    get: () => scheduleStore.getCommitTitle,
    set: (value) => scheduleStore.updateCommitTitle(value)
  });

  const commitMessage = computed({
    get: () => scheduleStore.getCommitMessage,
    set: (value) => scheduleStore.updateCommitMessage(value)
  });

  // Watchers
  watch(() => selectedSchedule.value, (newSchedule) => {
    if (!newSchedule) return;
    const scheduleTasks = scheduleStore.getScheduleTasksObj[newSchedule.id] || [];
    selectedScheduleContainsTasks.value = scheduleTasks.length > 0;
  });

  const handleTimeRangeTemplateUpdate = (template) => {
    selectedTimeRangeTemplate.value = template;
  };
  
  const importTasksToSchedules = async () => {
    // If the upload was a P6 XML with calendars, confirm/import them first so the
    // tasks can be re-linked to real calendar ids before the commit.
    if (p6Calendars.value.length) {
      showCalendarDialog.value = true;
      return;
    }
    await commitTasks();
  };

  const commitTasks = async () => {
    importingTasks.value = true;
    importCompleted.value = false;
    importError.value = null;

    // Stamp locks before commit: existing tasks echo their live lock (the
    // P6 XML path drops it, and a commit without editableTimeRangeTemplateIds
    // would lock every time range), new tasks get the confirmed selection.
    applyTaskLocks(nestedTasks.value, indexTasksById(taskStore.getTasks), editableTimeRangeIds.value);
    // A user-entered key from the upload step becomes each task's lock key.
    applyTaskKey(nestedTasks.value, uploadTaskKey.value);

    const importer = new ScheduleImporter(appStore.apiClient, settings.value);

    try {
      await importer.importTasks({
        scheduleId: selectedSchedule.value.id,
        tasks: nestedTasks.value,
        commitTitle: commitTitle.value,
        commitMessage: commitMessage.value,
        taskSetsPayload: taskSetsPayload.value,
        // Present the entered key alongside the processing keys (e.g. 'p6'),
        // so tasks locked under either may be updated.
        keys: [...new Set([uploadTaskKey.value, ...commitKeys.value].filter(Boolean))],
        // P6 ends are the source FinishDates — commit them verbatim.
        preserveEndDates: commitKeys.value.includes(P6_TASK_KEY)
      });

      importCompleted.value = true;
      commitTitle.value = '';
      commitMessage.value = '';
    } catch (error) {
      console.error("Error importing tasks:", error);
      importError.value = extractApiErrorMessage(error);
    }
  };

  // Failed commit acknowledged: close the dialog but keep the review step's
  // state so the user can fix the problem and retry the import.
  const handleImportFailed = () => {
    importingTasks.value = false;
    importError.value = null;
  };

  const handleCalendarsConfirmed = async ({ calendarsByP6Id }) => {
    // Re-link tasks to the newly created/updated calendars before committing.
    try {
      const calendarImporter = new CalendarImporter(appStore.apiClient, settings.value.projectCode);
      calendarImporter.relinkTasks(nestedTasks.value, taskCalendarLinks.value, calendarsByP6Id);
    } catch (error) {
      console.error('Error re-linking tasks to imported calendars:', error);
    }
    await commitTasks();
  };

  const handleCalendarsSkipped = async () => {
    // User skipped calendar import: tasks keep their fallback calendars.
    await commitTasks();
  };

const updateNestedTasks = (result) => {
  nestedTasks.value = result.nestedTasks;
  displayTasks.value = result.displayTasks;
  changes.value = result.changes;
  taskSetsPayload.value = result.taskSetsPayload;
  p6Calendars.value = result.p6Calendars || [];
  taskCalendarLinks.value = result.taskCalendarLinks || null;
  commitKeys.value = result.commitKeys || [];
  relationshipSummary.value = result.relationshipSummary || null;
  prunedEmptyWbsCount.value = result.prunedEmptyWbsCount || 0;
  durationSummary.value = result.durationSummary || null;
  const rangeTemplates = scheduleTimeRangeTemplates();
  timeRangeEditability.value = uploadAddsTasks(result.nestedTasks) && rangeTemplates.length > 0
    ? deriveTimeRangeEditability(taskStore.getTasks, rangeTemplates)
    : null;
};

const scheduleTimeRangeTemplates = () => {
  const templateId = selectedSchedule.value?.scheduleTemplateId;
  return scheduleStore.getScheduleTemplatesObj[templateId]?.[0]?.timeRangeTemplates || [];
};

// Does the processed tree contain any task the schedule doesn't already have?
const uploadAddsTasks = (tasks) => {
  const existingById = indexTasksById(taskStore.getTasks);
  let found = false;
  const walk = (list) => (list || []).forEach((task) => {
    if (found) return;
    if (!existingById.has(task.id)) {
      found = true;
      return;
    }
    walk(task.children);
  });
  walk(tasks);
  return found;
};
  
  const handleImportComplete = () => {
    importingTasks.value = false;
    currentStep.value = '1';
    selectedSchedule.value = null;
    nestedTasks.value = [];
    taskSetsPayload.value = {};
    selectedTimeRangeTemplate.value = null;
    p6Calendars.value = [];
    taskCalendarLinks.value = null;
    showCalendarDialog.value = false;
    commitKeys.value = [];
    uploadTaskKey.value = null;
    relationshipSummary.value = null;
    prunedEmptyWbsCount.value = 0;
    durationSummary.value = null;
    timeRangeEditability.value = null;
    editableTimeRangeIds.value = [];
  };
  </script>